import { ChevronForwardOutlineIc, ArrowBackOutlineIc } from '@dsch/dd-icons';
import { useEffect } from 'react';
import styled from 'styled-components';

export type ListItem = {
  value: string;
  displayName: string;
  formattedDisplayName?: string | null;
  group?: string;
  isDisabled?: boolean;
  displayBackIcon?: boolean;
};

export type ListItemVariant = {
  groupDisplayName: string;
  group: string;
  values: [
    {
      value: string;
      displayName: string;
      formattedDisplayName?: string;
      isDisabled?: boolean;
      displayBackIcon?: boolean;
    },
  ];
};

export interface CustomSelectProps {
  onSelect: (s: ListItem | ListItemVariant) => void;
  listData: (ListItem | ListItemVariant)[];
  preselectedOption?: ListItem;
  displayIcon?: boolean;
  hasHeading?: boolean;
}

const A11yButton = styled.button<{ isDefault?: boolean; isDisabled?: boolean }>`
  padding: 0;
  background-color: ${({ theme }) => theme.colors.WHITE};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  width: 100%;
  height: 40px;
  text-align: left;
  margin: ${({ isDefault }) => (isDefault ? `0 0 1px` : `1px 0`)};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.OFFWHITE};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.WHITE};
    background-image: none;
    box-shadow: none;
    border: none;
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.GREY_LIGHT};
  }
`;

const List = styled.ul`
  ${({ theme }) => theme.fontSize.M14};
  background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
`;

const ListItem = styled.li`
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ListHeadingItem = styled.li`
  ${({ theme }) => theme.fontSize.S12};
  padding: ${({ theme }) => `0 ${theme.spacing.M16}`};
  color: ${({ theme }) => theme.colors.GREY};
  margin: -1px 0;
`;

const SIoChevronForwardOutline = styled(ChevronForwardOutlineIc)`
  color: ${({ theme }) => theme.colors.GREY};
`;

const SArrowBackOutlineIc = styled(ArrowBackOutlineIc)`
  margin-right: ${({ theme }) => theme.spacing.S8};
`;

const DefaultText = styled.span`
  display: flex;
`;

function CustomSelect({
  listData,
  onSelect,
  preselectedOption,
  displayIcon,
  hasHeading = true,
}: CustomSelectProps) {
  const defaultItem = hasHeading ? listData[0] : undefined;
  const modifiedListData = hasHeading ? listData.slice(1) : listData;

  function handleClick(selectedOption: ListItem | ListItemVariant) {
    onSelect(selectedOption);
  }

  useEffect(() => {
    if (preselectedOption) {
      onSelect(preselectedOption);
    }
  }, []);

  return (
    <List>
      {/* Must determine which type of item is being passed: */}
      {defaultItem ? (
        'displayName' in defaultItem ? (
          <A11yButton
            isDefault
            onClick={() => handleClick(defaultItem)}
            key="default-item"
          >
            <ListItem role="button">
              <DefaultText>
                {defaultItem.displayBackIcon && (
                  <SArrowBackOutlineIc height={16} width={16} />
                )}
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      defaultItem.formattedDisplayName ||
                      defaultItem.displayName,
                  }}
                />
              </DefaultText>
            </ListItem>
          </A11yButton>
        ) : (
          <A11yButton
            isDefault
            onClick={() => handleClick(defaultItem.values[0])}
            key="default-item"
          >
            <ListItem>
              <DefaultText>
                {defaultItem.values[0].displayBackIcon && (
                  <SArrowBackOutlineIc height={16} width={16} />
                )}
                {defaultItem.values[0].displayName}
              </DefaultText>
            </ListItem>
          </A11yButton>
        )
      ) : undefined}
      {modifiedListData.map((item: ListItem | ListItemVariant, index) => {
        // ListItem:
        return 'displayName' in item ? (
          item.value ? (
            <A11yButton
              isDefault
              onClick={() => handleClick(item)}
              key={index}
              disabled={!!item.isDisabled}
            >
              <ListItem>
                <span
                  dangerouslySetInnerHTML={{
                    __html: item.formattedDisplayName || item.displayName,
                  }}
                />
                &nbsp;
                {displayIcon && (
                  <SIoChevronForwardOutline width="16" height="16" />
                )}
              </ListItem>
            </A11yButton>
          ) : (
            <ListHeadingItem key={index}>{item.displayName}</ListHeadingItem>
          )
        ) : (
          // ListItemVariant:
          [
            <ListHeadingItem key={index}>
              {item.groupDisplayName}
            </ListHeadingItem>,
            ...item.values.map((item, index) => (
              <A11yButton onClick={() => handleClick(item)} key={index}>
                <ListItem>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.formattedDisplayName || item.displayName,
                    }}
                  />
                  &nbsp;
                  {displayIcon && (
                    <SIoChevronForwardOutline width="16" height="16" />
                  )}
                </ListItem>
              </A11yButton>
            )),
          ]
        );
      })}
    </List>
  );
}

export { CustomSelect };
